import React, { useEffect, useMemo, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  setInput,
  setActiveAdvisor,
  setActiveProfessor,
  setAdvisorDraft,
} from '@/redux/slices/chatSlice'
import { Advisor } from '@/redux/types/advisor'
import { ActiveTab } from '@/constants/chat'
import { ChatbotType } from '@/redux/types/configuration'
import { escapeRegExp } from '@/lib/utils'
import { useGamePhase } from '@/hooks/useGamePhase'
import { Bot, HelpCircle, Zap } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'

type AdvisorRowProps = {}

const AdvisorRow: React.FC<AdvisorRowProps> = React.memo(() => {
  const dispatch = useAppDispatch()

  const advisors = useAppSelector((state) => state.advisor.advisors)
  const activeAdvisor = useAppSelector((state) => state.chat.activeAdvisor)
  const activeProfessor = useAppSelector((state) => state.chat.activeProfessor)
  const input = useAppSelector((state) => state.chat.input)
  const activeTab = useAppSelector((state) => state.chat.activeTab)
  const { isRandRPhase, isAdvisorPhase, manualMode, roundStatus } =
    useGamePhase()
  const { teamChatbots } = useAppSelector((state) => state.teamChatbot)

  const randrChatbot = useMemo(
    () => advisors.find((advisor) => advisor.chatbottype === ChatbotType.RANDR),
    [advisors]
  )
  const shouldAutoSelectRandR = useMemo(
    () =>
      isRandRPhase &&
      randrChatbot &&
      !activeProfessor &&
      !manualMode &&
      roundStatus !== ActivityState.Paused,
    [isRandRPhase, randrChatbot, activeProfessor, manualMode]
  )

  useEffect(() => {
    if (roundStatus === ActivityState.Paused) {
      dispatch(setInput(''))
      dispatch(setActiveProfessor(false))
      dispatch(setActiveAdvisor([]))
      dispatch(setAdvisorDraft(''))
    }
  }, [roundStatus, dispatch])

  useEffect(() => {
    if (
      shouldAutoSelectRandR &&
      !activeAdvisor.includes(randrChatbot!.chatbotid) &&
      roundStatus !== ActivityState.Paused
    ) {
      dispatch(setActiveAdvisor([randrChatbot!.chatbotid]))
    }
  }, [
    shouldAutoSelectRandR,
    randrChatbot,
    activeAdvisor,
    input,
    roundStatus,
    dispatch,
  ])

  const handleWhiteCellClick = useCallback(() => {
    if (activeProfessor) {
      dispatch(setActiveProfessor(false))
      dispatch(setActiveAdvisor([]))

      const allAdvisorNames = advisors.map((a) => a.chatbotname)
      let cleanMessage = input

      const allMentionsPattern = new RegExp(
        `@(${allAdvisorNames.map((name) => escapeRegExp(name)).join('|')}|white-cell)\\s*`,
        'gi'
      )

      cleanMessage = cleanMessage.replace(allMentionsPattern, '').trim()
      dispatch(setInput(cleanMessage))
      dispatch(setAdvisorDraft(cleanMessage))

      if (shouldAutoSelectRandR) {
        setTimeout(() => {
          dispatch(setActiveAdvisor([randrChatbot!.chatbotid]))
        }, 0)
      }
    } else {
      // If the white cell is not selected, select it and reset the advisor state
      dispatch(setActiveProfessor(true))
      dispatch(setActiveAdvisor([]))

      const allAdvisorNames = advisors.map((a) => a.chatbotname)
      let cleanMessage = input

      const allMentionsPattern = new RegExp(
        `@(${allAdvisorNames.map((name) => escapeRegExp(name)).join('|')}|white-cell)\\s*`,
        'gi'
      )

      cleanMessage = cleanMessage.replace(allMentionsPattern, '').trim()
      dispatch(setInput(cleanMessage))
      dispatch(setAdvisorDraft(cleanMessage))
    }
  }, [
    activeProfessor,
    input,
    advisors,
    shouldAutoSelectRandR,
    randrChatbot,
    dispatch,
  ])

  const handleAdvisorClick = useCallback(
    (advisor: Advisor) => {
      let updatedAdvisors = activeAdvisor
      const isAlreadySelected = activeAdvisor.includes(advisor.chatbotid)

      if (isAlreadySelected && (!isRandRPhase || manualMode)) {
        updatedAdvisors = updatedAdvisors.filter(
          (value) => value != advisor.chatbotid
        )
      } else if (!isAlreadySelected) {
        updatedAdvisors = [...activeAdvisor, advisor.chatbotid]
      }

      // If an advisor is selected, unselect white cell
      if (updatedAdvisors.length > 0) {
        dispatch(setActiveProfessor(false))
      }

      dispatch(setActiveAdvisor(updatedAdvisors))

      const allAdvisorNames = advisors.map((a) => a.chatbotname)
      let cleanMessage = input

      const allMentionsPattern = new RegExp(
        `@(${allAdvisorNames.map((name) => escapeRegExp(name)).join('|')}|white-cell)\\s*`,
        'gi'
      )

      // Remove all advisor mentions from the input
      cleanMessage = cleanMessage.replace(allMentionsPattern, '').trim()

      dispatch(setInput(cleanMessage))
      dispatch(setAdvisorDraft(cleanMessage))
    },
    [activeAdvisor, isRandRPhase, manualMode, advisors, input, dispatch]
  )

  const handleAssignedChatbotClick = useCallback(
    (chatbot: { chatbot: string; chatbot_name: string }) => {
      let updatedAdvisors = activeAdvisor
      if (activeAdvisor.includes(chatbot.chatbot)) {
        updatedAdvisors = updatedAdvisors.filter(
          (value) => value != chatbot.chatbot
        )
      } else {
        updatedAdvisors = [...activeAdvisor, chatbot.chatbot]
      }

      // If an advisor is selected, unselect white cell
      if (updatedAdvisors.length > 0) {
        dispatch(setActiveProfessor(false))
      }

      dispatch(setActiveAdvisor(updatedAdvisors))

      const allAdvisorNames = [
        ...teamChatbots.map((a) => a.chatbot_name),
        ...advisors.map((a) => a.chatbotname),
      ]
      let cleanMessage = input

      // Create pattern to match all possible advisor mentions
      const allMentionsPattern = new RegExp(
        `@(${allAdvisorNames.map((name) => escapeRegExp(name)).join('|')}|white-cell)\\s*`,
        'gi'
      )

      // Remove all advisor mentions from the input
      cleanMessage = cleanMessage.replace(allMentionsPattern, '').trim()

      dispatch(setInput(cleanMessage))
      dispatch(setAdvisorDraft(cleanMessage))
    },
    [activeAdvisor, teamChatbots, advisors, input, dispatch]
  )

  const showAdvisorTab = activeTab === ActiveTab.Advisor
  const showRandRChatbot = showAdvisorTab && randrChatbot && isRandRPhase
  const showAssignedChatbots = showAdvisorTab && isAdvisorPhase

  return (
    <div className='flex overflow-x-auto gap-5 items-center py-2 px-2'>
      {showAdvisorTab && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeProfessor ? 'default' : 'outline'}
                size='sm'
                onClick={handleWhiteCellClick}
                className={`transition-all duration-200 hover:scale-105 rounded-full ${
                  activeProfessor
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-2 border-emerald-300 font-bold shadow-lg ring-2 ring-emerald-200'
                    : 'bg-gradient-to-r from-slate-50 to-slate-100 hover:from-emerald-50 hover:to-teal-50 text-slate-700 border border-slate-300 hover:border-emerald-200 hover:shadow-md'
                }`}
              >
                <HelpCircle className='w-4 h-4' />
                Ask white-cell
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Chat with professor for guidance</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {showRandRChatbot && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled={roundStatus === ActivityState.Paused}
                key={randrChatbot.chatbotid}
                variant={
                  activeAdvisor.includes(randrChatbot.chatbotid)
                    ? 'default'
                    : 'ghost'
                }
                size='sm'
                onClick={() => handleAdvisorClick(randrChatbot)}
                className={`transition-all duration-200 hover:scale-105 rounded-lg ${
                  activeAdvisor.includes(randrChatbot.chatbotid)
                    ? 'bg-gradient-to-r from-indigo-500 to-dark-blue hover:from-indigo-600 hover:to-dark-blue-hovered text-white font-bold shadow-md border border-indigo-300'
                    : 'text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-300 hover:border-indigo-200'
                }`}
              >
                <Zap className='w-4 h-4' />@{randrChatbot.chatbotname}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Research & Development AI Assistant</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {showAssignedChatbots &&
        teamChatbots.map(
          (chatbot: { chatbot: string; chatbot_name: string }) => (
            <TooltipProvider key={chatbot.chatbot}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    disabled={roundStatus === ActivityState.Paused}
                    variant={
                      activeAdvisor.includes(chatbot.chatbot)
                        ? 'default'
                        : 'ghost'
                    }
                    size='sm'
                    onClick={() => handleAssignedChatbotClick(chatbot)}
                    className={`transition-all duration-200 hover:scale-105 rounded-lg ${
                      activeAdvisor.includes(chatbot.chatbot)
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold shadow-md border border-orange-300'
                        : 'text-slate-500 hover:text-orange-600 hover:bg-orange-50 border border-slate-300 hover:border-orange-200'
                    }`}
                  >
                    <Bot className='w-4 h-4' />@{chatbot.chatbot_name}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Team AI Assistant - {chatbot.chatbot_name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        )}
    </div>
  )
})

AdvisorRow.displayName = 'AdvisorRow'

export default AdvisorRow
